import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';
import { getAllSalons } from '../firebase/salon';
import { Card, Button } from '../components/ui';

/**
 * Página de login para acesso ao sistema
 * Permite que o usuário faça login ou acesse a página de cadastro
 */
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [salons, setSalons] = useState([]);
  const [loadingSalons, setLoadingSalons] = useState(true);
  const [hasRedirected, setHasRedirected] = useState(false);
  
  const { login, isAuthenticated } = useAuth();
  const { 
    userSalons, 
    loading: userLoading, 
    selectedSalonId, 
    refreshUserData, 
    getCurrentRole 
  } = useUser();
  const navigate = useNavigate();

  // Redirecionar se já estiver autenticado
  useEffect(() => {
    let isMounted = true;
    
    // Se o usuário estiver autenticado, verificar o papel e redirecionar
    if (isAuthenticated && !userLoading && !hasRedirected) {
      console.log(`[LoginPage] Usuário autenticado, aguardando inicialização do contexto. 
        isAuthenticated: ${isAuthenticated}
        userLoading: ${userLoading}
        hasRedirected: ${hasRedirected}
        userSalons: ${userSalons?.length || 0}
        selectedSalonId: ${selectedSalonId || 'não selecionado'}
      `);
      
      const handleRedirect = async () => {
        // Prevenir múltiplos redirecionamentos
        if (hasRedirected) {
          console.log('[LoginPage] Redirecionamento já realizado, ignorando');
          return;
        }
        
        try {
          console.log(`[LoginPage] Verificando redirecionamento: userSalons=${userSalons?.length || 0}, selectedSalonId=${selectedSalonId || 'não selecionado'}`);
          
          // Verificar se precisamos obter os dados do usuário
          if (!userSalons || userSalons.length === 0) {
            console.log('[LoginPage] Dados de salão não disponíveis, aguardando...');
            return; // Sair e aguardar próxima execução quando dados estiverem disponíveis
          }
          
          // Verificar papel do usuário e redirecionar
          const role = getCurrentRole();
          console.log(`[LoginPage] Papel do usuário: ${role}`);
          
          if (!isMounted) {
            console.log('[LoginPage] Componente desmontado, cancelando redirecionamento');
            return;
          }
          
          // Marcar como redirecionado para evitar múltiplas chamadas
          setHasRedirected(true);
          
          // Redirecionar com base no papel
          if (role === 'professional') {
            console.log('[LoginPage] Redirecionando para painel profissional');
            navigate('/professional', { replace: true });
          } else if (['owner', 'admin', 'manager'].includes(role)) {
            console.log(`[LoginPage] Redirecionando para painel admin: ${selectedSalonId}`);
            navigate(`/admin/${selectedSalonId}/appointments`, { replace: true });
          } else {
            console.log(`[LoginPage] Papel desconhecido: ${role}, redirecionando para seleção de salão`);
            navigate('/salon-selection', { replace: true });
          }
        } catch (error) {
          console.error('[LoginPage] Erro ao redirecionar:', error);
          if (isMounted && !hasRedirected) {
            setError('Erro ao carregar seus dados. Por favor, tente novamente.');
            // Apenas uma tentativa de redirecionamento para seleção de salão
            setHasRedirected(true);
            navigate('/salon-selection', { replace: true });
          }
        }
      };
      
      // Definir um único timeout para tentar o redirecionamento
      const timeoutId = setTimeout(handleRedirect, 1000);
      
      return () => {
        console.log('[LoginPage] Desmontando componente, limpando timeout');
        isMounted = false;
        clearTimeout(timeoutId);
      };
    }
  }, [isAuthenticated, userLoading, userSalons, navigate, getCurrentRole, selectedSalonId, hasRedirected]);

  // Carregar lista de salões disponíveis
  useEffect(() => {
    const fetchSalons = async () => {
      try {
        setLoadingSalons(true);
        const salonsData = await getAllSalons();
        setSalons(salonsData);
      } catch (err) {
        console.error('Erro ao carregar salões:', err);
        setError('Erro ao carregar lista de salões.');
      } finally {
        setLoadingSalons(false);
      }
    };

    fetchSalons();
  }, []);

  // Função para fazer login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    try {
      console.log('[LoginPage] Iniciando processo de login');
      const success = await login(email, password);
      
      if (success) {
        console.log('[LoginPage] Login bem-sucedido, aguardando dados do usuário');
        // O redirecionamento será feito pelo efeito que observa isAuthenticated
      } else {
        console.log('[LoginPage] Falha no login');
        setError('Email ou senha incorretos. Tente novamente.');
      }
    } catch (err) {
      console.error('[LoginPage] Erro no login:', err);
      setError('Ocorreu um erro ao fazer login. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Login rápido para um salão específico
  const handleQuickLogin = (salonId) => {
    // Como estamos em desenvolvimento, só redirecionamos para a página do salão
    // sem necessidade de autenticação completa
    navigate(`/${salonId}/admin`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-500 to-blue-700 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        <div className="bg-white rounded-lg shadow-xl overflow-hidden mb-6">
          <div className="p-6 sm:p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-800">Bookly</h1>
              <p className="text-gray-600 mt-2">Sistema de Agendamento</p>
            </div>
            
            {error && (
              <div className="mb-6 p-3 bg-red-100 text-red-700 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Formulário de login padrão */}
              <div>
                <h2 className="text-xl font-semibold mb-4">Login Convencional</h2>
                <form onSubmit={handleLogin} className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                      Senha
                    </label>
                    <input
                      id="password"
                      type="password"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                  
                  <div>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading}
                      fullWidth
                    >
                      {loading ? 'Entrando...' : 'Entrar'}
                    </Button>
                  </div>
                  
                  <div className="text-center text-sm text-gray-600 mt-4">
                    <p>
                      Não tem uma conta?{' '}
                      <Link to="/signup" className="text-blue-600 hover:text-blue-800 font-medium">
                        Criar novo salão
                      </Link>
                    </p>
                  </div>
                </form>
              </div>
              
              {/* Login rápido para salões existentes */}
              <div>
                <h2 className="text-xl font-semibold mb-4">Acesso Rápido (Modo Desenvolvimento)</h2>
                
                {loadingSalons ? (
                  <div className="flex justify-center items-center h-60">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : salons.length === 0 ? (
                  <div className="bg-yellow-50 p-4 rounded-md text-yellow-700">
                    Nenhum salão encontrado. Crie um novo salão para começar.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 max-h-96 overflow-y-auto p-2">
                    {salons.map(salon => (
                      <Card 
                        key={salon.id} 
                        variant="outline" 
                        hoverable 
                        onClick={() => handleQuickLogin(salon.id)}
                        className="transition-all duration-200 cursor-pointer"
                      >
                        <div className="p-4">
                          <div className="flex items-center">
                            {salon.logoUrl && (
                              <img 
                                src={salon.logoUrl} 
                                alt={salon.name || salon.id} 
                                className="w-10 h-10 rounded-full object-cover mr-3 bg-gray-100"
                              />
                            )}
                            <div>
                              <h3 className="font-medium text-gray-900">{salon.name || salon.id}</h3>
                              {salon.address && typeof salon.address === 'string' ? (
                                <p className="text-xs text-gray-500">{salon.address}</p>
                              ) : salon.address && typeof salon.address === 'object' ? (
                                <p className="text-xs text-gray-500">
                                  {salon.address.formattedAddress || 
                                   `${salon.address.street || ''}, ${salon.address.number || ''} - ${salon.address.city || ''}`}
                                </p>
                              ) : null}
                            </div>
                          </div>
                          <div className="mt-3 text-right">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/${salon.id}`);
                              }}
                            >
                              Cliente
                            </Button>
                            <Button 
                              variant="primary" 
                              size="sm"
                              className="ml-2"
                            >
                              Admin
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 